import React, { useEffect, useMemo, useState } from "react";

import "./LocationsForm.css";
import "../CompanyInfoForm.css";
import { CheckCircleFilled, DeleteFilled } from "@ant-design/icons";
import { Button, Form } from "antd";
import { useForm } from "antd/es/form/Form";
import { toast } from "react-toastify";

import AddLocationChoice from "./AddLocationChoice";
import AddLocationDetails from "./AddLocationDetails";
import LocationBillingDetails from "./LocationBillingDetails";
import LocationSampleDetails from "./LocationSampleDetails";
import FormButtons from "../ReusableComponents/FormButtons";
import MyForm from "../../../MyForm";
import useHandleStepChange from "../../useHandleStepChange";

import { ReactComponent as BuildingIcon } from "../../../../../assets/images/subscriptions/building-line.svg";
import { ReactComponent as MapPinIcon } from "../../../../../assets/images/subscriptions/map-pin-line.svg";
import { ReactComponent as ReceiptIcon } from "../../../../../assets/images/subscriptions/receipt-line.svg";

const DUPLICATE_COMPANY_NAME_MESSAGE = "Company name already exists. Please enter a different company name.";

function normalizeCompanyName(name) {
  return (name || "").trim().toLowerCase();
}

function getLocationCompanyName(location) {
  return location?.company_name || "";
}

/** True when companyName matches another location (case-insensitive), excluding excludeId when editing. */
function isDuplicateCompanyName(companyName, locations = [], excludeId = null) {
  const normalized = normalizeCompanyName(companyName);
  if (!normalized) return false;
  return locations.some((loc) => {
    if (excludeId && loc.id === excludeId) return false;
    return normalizeCompanyName(getLocationCompanyName(loc)) === normalized;
  });
}

function hasDuplicateCompanyNames(locations = []) {
  const seen = new Set();
  return locations.some((loc) => {
    const normalized = normalizeCompanyName(getLocationCompanyName(loc));
    if (!normalized) return false;
    if (seen.has(normalized)) return true;
    seen.add(normalized);
    return false;
  });
}

const PRODUCT_SAMPLE_DEFAULTS = {
  sample_supply: undefined,
  sample_type: "Sample Type",
  date: "Received Date",
  physical_category: "Physical Category",
  editable_fields: ["Item", "Lot", "Description"],
  product_link: undefined,
};

function hasSupplyChoice(info, key) {
  return info?.[key] === "Yes" || info?.[key] === "No";
}

function editableFieldTitles(fields) {
  if (!Array.isArray(fields) || !fields.length) return null;
  const titles = fields.map((field) => {
    if (typeof field === "string") return field;
    return field?.title_field || field?.json_field || "";
  }).filter(Boolean);
  return titles.length ? titles : null;
}

function linkFieldValues(link) {
  if (link == null || link === "") return [];
  return [].concat(link).map((item) => {
    if (typeof item === "string") return item;
    return item?.json_field || item?.value || "";
  }).filter(Boolean);
}

function isSavedBillingContact(contact) {
  if (!contact) return false;
  return Boolean(
    contact.position
    || contact.phone_number
    || contact.billing?.ap_contact
    || contact.billing?.phone_number
  );
}

const EMPTY_BILLING_CONTACT = {
  same_as_address: false,
  contact_name: "",
  position: "",
  email: "",
  phone_number: "",
  billing: {
    bill_to: "",
    ap_contact: "",
    email: "",
    phone_number: "",
    address: {},
  },
};

const ENVIRONMENT_SAMPLE_DEFAULTS = {
  env_sample_supply: undefined,
  sample_type: "Sample Type",
  date: "Received Date",
  physical_category: "Physical Category",
  swab_number: "Swab Number",
  zone: "Zone",
  section: "Section",
  editable_fields: [],
  environment_link: [],
  environment_supplies_needed: undefined,
};

// const MAX_LOCATIONS = 5; // TODO: re-enable 5-location cap on Add More Location

function formatAddressLine(address = {}) {
  const street = [address.street, address.line_2].filter(Boolean).join(", ");
  return street || "—";
}

function formatAddressMeta(address = {}) {
  const {
    city, state, country, zipcode,
  } = address;
  return [city, state, country, zipcode].filter(Boolean).join(" • ") || "—";
}

function hasAddressContent(address) {
  if (!address || typeof address !== "object") return false;
  return Object.values(address).some((value) => String(value || "").trim());
}

const REQUIRED_ADDRESS_FIELDS = ["street", "zipcode", "city", "country", "state"];

function isAddressComplete(address) {
  if (!address || typeof address !== "object") return false;
  return REQUIRED_ADDRESS_FIELDS.every((key) => String(address[key] || "").trim());
}

/** A location entry is finished when both addresses are filled and the user has saved it. */
function isLocationComplete(location) {
  return !location?.pendingCompletion
    && isAddressComplete(location?.address)
    && isAddressComplete(location?.billingAddress);
}

function getBillingAddressForDisplay(location) {
  return hasAddressContent(location?.billingAddress) ? location.billingAddress : {};
}

/** Primary billing: Genista/contact form wins (invitation API has no billing_address). */
function resolvePrimaryBillingAddress(location, contactInfo, companyInfo) {
  if (contactInfo?.same_as_address) {
    if (hasAddressContent(location?.address)) return location.address;
    if (hasAddressContent(companyInfo?.address)) return companyInfo.address;
  }
  if (hasAddressContent(contactInfo?.billing?.address)) {
    return contactInfo.billing.address;
  }
  // Existing-customer API seed only (customerinvitation does not return billing)
  if (hasAddressContent(location?.billingAddress)) {
    return location.billingAddress;
  }
  return null;
}

function LocationCard({
  location, onClick, onDelete, canDelete, incomplete,
}) {
  return (
    <div
      className={`LocationsForm__Card LocationsForm__Card--clickable${incomplete ? " LocationsForm__Card--incomplete" : ""}`}
      role="button"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          if (onClick) onClick();
        }
      }}
    >
      <div className="LocationsForm__CardHeader">
        <div className="LocationsForm__CardTitle">
          <BuildingIcon className="LocationsForm__BuildingIcon" />
          <span>
            {location.name}
            {location.location_name ? (
              <span className="LocationsForm__SiteName">{location.location_name}</span>
            ) : null}
          </span>
        </div>
        <div className="LocationsForm__CardHeaderRight">
          <div className="LocationsForm__Badges">
            {location.hasProduct && (
              <span className="LocationsForm__Badge">
                <CheckCircleFilled className="LocationsForm__BadgeCheck" />
                Product
              </span>
            )}
            {location.hasEnvironment && (
              <span className="LocationsForm__Badge">
                <CheckCircleFilled className="LocationsForm__BadgeCheck" />
                Environment
              </span>
            )}
          </div>
          {canDelete && (
            <button
              type="button"
              className="LocationsForm__DeleteBtn"
              aria-label={`Delete ${location.name || "location"}`}
              onClick={(e) => {
                e.stopPropagation();
                if (onDelete) onDelete(location);
              }}
            >
              <DeleteFilled className="LocationsForm__DeleteIcon" />
            </button>
          )}
        </div>
      </div>
      <div className="LocationsForm__CardBody">
        <div className="LocationsForm__AddressCol">
          <div className="LocationsForm__AddressLabel">
            <MapPinIcon className="LocationsForm__AddressIcon" />
            Location
          </div>
          <p className="LocationsForm__AddressStreet">{formatAddressLine(location.address)}</p>
          <p className="LocationsForm__AddressMeta">{formatAddressMeta(location.address)}</p>
        </div>
        <div className="LocationsForm__Divider" />
        <div className="LocationsForm__AddressCol">
          <div className="LocationsForm__AddressLabel">
            <ReceiptIcon className="LocationsForm__AddressIcon LocationsForm__AddressIcon--receipt" />
            Billing Address
          </div>
          <p className="LocationsForm__AddressStreet">{formatAddressLine(getBillingAddressForDisplay(location))}</p>
          <p className="LocationsForm__AddressMeta">{formatAddressMeta(getBillingAddressForDisplay(location))}</p>
        </div>
      </div>
    </div>
  );
}

function buildPrimaryLocation(companyInfo, contactInfo, fieldsInfo, managementType) {
  const address = companyInfo?.address || {};
  const billingAddress = resolvePrimaryBillingAddress(
    { address, billingAddress: null },
    contactInfo,
    companyInfo,
  );
  return {
    id: "primary",
    name: companyInfo?.company_name || "Primary Location",
    company_name: companyInfo?.company_name || "",
    address,
    billingAddress,
    hasProduct: fieldsInfo?.product_info?.sample_supply === "Yes",
    hasEnvironment: fieldsInfo?.environment_info?.env_sample_supply === "Yes",
    managementType,
  };
}

export default function LocationsForm({
  initialValues,
  companyInfo,
  contactInfo,
  fieldsInfo,
  onNextButton,
  onBackButton,
  onFinishFailed,
  stepChangeInProgress,
  handleFormStepChange,
  handleSubmit,
  addCompany = false,
  submitting = false,
  onLocationsSidebarChange,
  emailValidator,
  phoneValidator,
  primaryManagementType,
  canAddLocation = true,
  isSubscriptionCustomer = false,
  skipManagementChoice = false,
}) {
  const FORM_NAME = "locations_form";
  const [form] = useForm();
  const [view, setView] = useState("list");
  const [managementType, setManagementType] = useState(null);
  const [pendingDetails, setPendingDetails] = useState(null);
  const [pendingProductInfo, setPendingProductInfo] = useState(null);
  const [pendingEnvironmentInfo, setPendingEnvironmentInfo] = useState(null);
  const [pendingContactInfo, setPendingContactInfo] = useState(null);
  const [editingLocationId, setEditingLocationId] = useState(null);
  const [showIncomplete, setShowIncomplete] = useState(false);
  const [locations, setLocations] = useState(() => {
    if (!initialValues?.locations?.length) {
      return [buildPrimaryLocation(companyInfo, contactInfo, fieldsInfo, primaryManagementType)];
    }
    return initialValues.locations.map((location, index) => {
      if (index !== 0) return location;
      const address = hasAddressContent(companyInfo?.address) ? companyInfo.address : location.address;
      return {
        ...location,
        product_info: undefined,
        environment_info: undefined,
        contact_info: undefined,
        editedOnCard: false,
        name: companyInfo?.company_name || location.name,
        company_name: companyInfo?.company_name || location.company_name,
        address,
        managementType: primaryManagementType ?? location.managementType,
        billingAddress: resolvePrimaryBillingAddress(
          { ...location, address },
          contactInfo,
          companyInfo,
        ),
        hasProduct: fieldsInfo?.product_info?.sample_supply === "Yes",
        hasEnvironment: fieldsInfo?.environment_info?.env_sample_supply === "Yes",
      };
    });
  });

  const isAddFlowView = view === "add"
    || view === "details"
    || view === "product-details"
    || view === "environment-details"
    || view === "billing";
  const isEditing = Boolean(editingLocationId);

  useHandleStepChange(FORM_NAME, form, stepChangeInProgress, handleFormStepChange, () => ({ locations }));

  useEffect(() => {
    if (onLocationsSidebarChange) {
      onLocationsSidebarChange({
        locations,
        isAdding: isAddFlowView && view !== "list" && !isEditing,
        editingLocationId: isEditing ? editingLocationId : null,
      });
    }
  }, [locations, view, onLocationsSidebarChange, isAddFlowView, isEditing, editingLocationId]);

  useEffect(() => () => {
    if (onLocationsSidebarChange) {
      onLocationsSidebarChange({ locations: [], isAdding: false });
    }
  }, [onLocationsSidebarChange]);

  const layout = useMemo(() => ({
    labelCol: { span: 24 },
    wrapperCol: { span: 24 },
    layout: "vertical",
  }), []);

  const resetAddFlow = () => {
    setManagementType(null);
    setPendingDetails(null);
    setPendingProductInfo(null);
    setPendingEnvironmentInfo(null);
    setPendingContactInfo(null);
    setEditingLocationId(null);
  };

  const buildBillingDefaults = (details = {}, contactOverride = null) => {
    if (contactOverride) {
      return {
        same_as_address: contactOverride.same_as_address ?? true,
        contact_name: contactOverride.contact_name || details.customer_name || "",
        position: contactOverride.position || "",
        email: contactOverride.email || details.customer_email || "",
        phone_number: contactOverride.phone_number || "",
        billing: contactOverride.billing || {
          bill_to: "",
          ap_contact: "",
          email: details.customer_email || "",
          phone_number: "",
          address: { ...(details.address || {}) },
        },
      };
    }
    return {
      same_as_address: true,
      contact_name: details.customer_name || "",
      position: "",
      email: details.customer_email || "",
      phone_number: "",
      billing: {
        bill_to: "",
        ap_contact: "",
        email: details.customer_email || "",
        phone_number: "",
        address: { ...(details.address || {}) },
      },
    };
  };

  const isSecondaryLocation = (location) => (
    Boolean(location) && locations.findIndex((loc) => loc.id === location.id) > 0
  );

  const editingLocation = isEditing
    ? locations.find((loc) => loc.id === editingLocationId)
    : null;

  const billingInitialValues = () => {
    if (!pendingContactInfo && isSecondaryLocation(editingLocation)) {
      return EMPTY_BILLING_CONTACT;
    }
    return buildBillingDefaults(pendingDetails || {}, pendingContactInfo);
  };

  const buildLocationDetailsDefaults = () => ({
    company_name: "",
    customer_name: companyInfo?.customer_name || "",
    customer_email: companyInfo?.customer_email || "",
    distribution_list: companyInfo?.distribution_list?.length
      ? companyInfo.distribution_list
      : [companyInfo?.customer_email].filter(Boolean),
    address: {
      street: "",
      line_2: "",
      zipcode: "",
      city: "",
      state: "",
      country: "",
    },
  });

  const buildLocationDetailsFromLocation = (location) => ({
    company_name: location.company_name || "",
    customer_name: location.customer_name || companyInfo?.customer_name || "",
    customer_email: location.customer_email || companyInfo?.customer_email || "",
    distribution_list: location.distribution_list?.length
      ? location.distribution_list
      : (companyInfo?.distribution_list?.length
        ? companyInfo.distribution_list
        : [companyInfo?.customer_email].filter(Boolean)),
    address: location.address || {},
  });

  /** Prefill add-location flow from location 1 (primary company / current account). */
  const buildPrefillFromPrimaryLocation = () => {
    const primary = locations[0] || {};
    const details = {
      ...buildLocationDetailsFromLocation({
        ...primary,
        address: primary.address || companyInfo?.address || {},
      }),
      // Require a unique company name for each additional location
      company_name: "",
    };
    const contactFromPrimary = primary.contact_info || (
      contactInfo
        ? {
          same_as_address: contactInfo.same_as_address,
          contact_name: contactInfo.contact_name,
          position: contactInfo.position,
          email: contactInfo.email,
          phone_number: contactInfo.phone_number,
          billing: contactInfo.billing,
        }
        : null
    );
    return {
      details,
      productInfo: primary.product_info || fieldsInfo?.product_info || null,
      environmentInfo: primary.environment_info || fieldsInfo?.environment_info || null,
      contactInfo: contactFromPrimary,
    };
  };

  const applyExistingAccountPrefill = () => {
    const prefill = buildPrefillFromPrimaryLocation();
    setPendingDetails(prefill.details);
    setPendingProductInfo(prefill.productInfo);
    setPendingEnvironmentInfo(prefill.environmentInfo);
    setPendingContactInfo(prefill.contactInfo);
    form.setFieldsValue(prefill.details);
  };

  const handleOpenAdd = () => {
    resetAddFlow();
    if (skipManagementChoice) {
      setManagementType("independent");
      form.setFieldsValue(buildLocationDetailsDefaults());
      setView("details");
      return;
    }
    setView("add");
  };

  const canDeleteLocation = (location, index) => {
    // First location entered during onboarding is never deletable
    if (index === 0) return false;
    // Subscription-seeded locations from invitation location_address cannot be deleted
    if (isSubscriptionCustomer && location.seededFromInvitation) return false;
    return true;
  };

  const handleDeleteLocation = (locationToDelete) => {
    const index = locations.findIndex((loc) => loc.id === locationToDelete.id);
    if (!canDeleteLocation(locationToDelete, index)) return;
    setLocations((prev) => prev.filter((loc) => loc.id !== locationToDelete.id));
  };

  const handleCancelAdd = () => {
    resetAddFlow();
    setView("list");
  };

  const savedSampleInfo = (location, kind) => {
    const supplyKey = kind === "product" ? "sample_supply" : "env_sample_supply";
    const own = kind === "product" ? location?.product_info : location?.environment_info;
    if (hasSupplyChoice(own, supplyKey)) return own;
    // Secondary locations only show what the user entered for that location
    if (isSecondaryLocation(location)) return null;
    const shared = kind === "product" ? fieldsInfo?.product_info : fieldsInfo?.environment_info;
    return shared || own || null;
  };

  const savedContactInfo = (location) => {
    if (isSecondaryLocation(location)) {
      return location.pendingCompletion ? null : (location.contact_info || null);
    }
    if (isSavedBillingContact(location?.contact_info)) return location.contact_info;
    const base = contactInfo || location?.contact_info || null;
    if (!base) return null;
    const locationAddress = hasAddressContent(location?.address) ? location.address : null;
    const billingAddress = hasAddressContent(location?.billingAddress)
      ? location.billingAddress
      : locationAddress;
    return {
      ...base,
      same_as_address: location?.contact_info?.same_as_address ?? base.same_as_address ?? true,
      billing: {
        ...(base.billing || {}),
        address: billingAddress || base.billing?.address || {},
      },
    };
  };

  const handleOpenEdit = (location) => {
    resetAddFlow();
    const details = buildLocationDetailsFromLocation(location);
    setEditingLocationId(location.id);
    setManagementType(location.managementType);
    setPendingDetails(details);
    setPendingProductInfo(savedSampleInfo(location, "product"));
    setPendingEnvironmentInfo(savedSampleInfo(location, "environment"));
    setPendingContactInfo(savedContactInfo(location));
    setView("details");
    form.setFieldsValue(details);
  };

  useEffect(() => {
    if (view === "details") {
      form.setFieldsValue(pendingDetails || buildLocationDetailsDefaults());
    }
    if (view === "product-details") {
      form.setFieldsValue({
        ...PRODUCT_SAMPLE_DEFAULTS,
        ...(pendingProductInfo || {}),
        sample_supply: pendingProductInfo?.sample_supply || undefined,
        editable_fields: editableFieldTitles(pendingProductInfo?.editable_fields)
          || PRODUCT_SAMPLE_DEFAULTS.editable_fields,
        product_link: linkFieldValues(pendingProductInfo?.product_link),
      });
    }
    if (view === "environment-details") {
      form.setFieldsValue({
        ...ENVIRONMENT_SAMPLE_DEFAULTS,
        ...(pendingEnvironmentInfo || {}),
        env_sample_supply: pendingEnvironmentInfo?.env_sample_supply || undefined,
        editable_fields: editableFieldTitles(pendingEnvironmentInfo?.editable_fields)
          || ENVIRONMENT_SAMPLE_DEFAULTS.editable_fields,
        environment_link: linkFieldValues(pendingEnvironmentInfo?.environment_link),
      });
    }
    if (view === "billing") {
      form.setFieldsValue(billingInitialValues());
    }
  }, [view, pendingDetails, pendingProductInfo, pendingEnvironmentInfo, pendingContactInfo, companyInfo, form]); // eslint-disable-line react-hooks/exhaustive-deps

  const toEditableFieldObjects = (fields = []) => (
    fields.filter(Boolean).map((field) => {
      if (typeof field === "object") return field;
      const title = field;
      const json = title.trim().replace(/[^\w\s]/gi, " ").replace(/\s+/g, "_").toLowerCase()
        .replace(/(?:__+)/g, "_")
        .replace(/^_+|_+$/g, "");
      return { json_field: json, title_field: title };
    })
  );

  const handleSaveLocation = (
    details = {},
    productInfo = null,
    environmentInfo = null,
    billingInfo = null,
  ) => {
    // New locations must pick a management type. Editing a seeded location can save without one.
    if (!isEditing && !managementType) return;
    if (isDuplicateCompanyName(
      details.company_name,
      locations,
      isEditing ? editingLocationId : null,
    )) {
      toast.error(DUPLICATE_COMPANY_NAME_MESSAGE);
      return;
    }
    const address = details.address || companyInfo?.address || {};
    const existingPreview = isEditing
      ? locations.find((loc) => loc.id === editingLocationId)
      : null;
    // Billing address only from the billing information step (or explicit billing payload)
    let billingAddress = null;
    if (billingInfo) {
      if (billingInfo.same_as_address) {
        billingAddress = hasAddressContent(address) ? address : null;
      } else if (hasAddressContent(billingInfo.billing?.address)) {
        billingAddress = billingInfo.billing.address;
      }
    } else if (isEditing && hasAddressContent(existingPreview?.billingAddress)) {
      billingAddress = existingPreview.billingAddress;
    } else if (hasAddressContent(contactInfo?.billing?.address)) {
      billingAddress = contactInfo.billing.address;
    }
    const hasProduct = productInfo
      ? productInfo.sample_supply === "Yes"
      : fieldsInfo?.product_info?.sample_supply === "Yes";
    const hasEnvironment = environmentInfo
      ? environmentInfo.env_sample_supply === "Yes"
      : fieldsInfo?.environment_info?.env_sample_supply === "Yes";

    const contact_info = billingInfo
      ? {
        contact_name: billingInfo.contact_name,
        position: billingInfo.position,
        email: billingInfo.email,
        phone_number: billingInfo.phone_number,
        same_as_address: billingInfo.same_as_address,
        billing: billingInfo.billing,
      }
      : undefined;

    setLocations((prev) => {
      const existing = isEditing
        ? prev.find((loc) => loc.id === editingLocationId)
        : null;
      const nextLocation = {
        ...(existing || {}),
        id: isEditing ? editingLocationId : `loc-${Date.now()}`,
        name: details.company_name
          || (isEditing ? existing?.name : null)
          || `Location ${prev.length + 1}`,
        address,
        billingAddress,
        hasProduct,
        hasEnvironment,
        managementType,
        company_name: details.company_name,
        customer_name: details.customer_name,
        customer_email: details.customer_email,
        distribution_list: details.distribution_list,
        product_info: productInfo || existing?.product_info,
        environment_info: environmentInfo || existing?.environment_info,
        contact_info: contact_info
          || (isEditing ? existing?.contact_info : undefined),
        location_name: existing?.location_name,
        seededFromInvitation: existing?.seededFromInvitation,
        pendingCompletion: false,
        editedOnCard: true,
      };

      if (isEditing) {
        return prev.map((loc) => (loc.id === editingLocationId ? nextLocation : loc));
      }
      return [...prev, nextLocation];
    });
    resetAddFlow();
    setView("list");
  };

  const handleFinish = (formValues) => {
    if (view === "add") {
      if (managementType === "existing") {
        applyExistingAccountPrefill();
        setView("details");
        return;
      }
      if (managementType === "independent") {
        form.setFieldsValue(buildLocationDetailsDefaults());
        setView("details");
        return;
      }
      return;
    }

    if (view === "details") {
      if (isDuplicateCompanyName(
        formValues.company_name,
        locations,
        isEditing ? editingLocationId : null,
      )) {
        toast.error(DUPLICATE_COMPANY_NAME_MESSAGE);
        form.setFields([{
          name: "company_name",
          errors: [DUPLICATE_COMPANY_NAME_MESSAGE],
        }]);
        return;
      }
      // Card edit always continues through product, environment, and billing.
      setPendingDetails(formValues);
      setView("product-details");
      return;
    }

    if (view === "product-details") {
      const productInfo = {
        sample_supply: formValues.sample_supply,
        sample_type: formValues.sample_type,
        date: formValues.date,
        physical_category: formValues.physical_category,
        editable_fields: toEditableFieldObjects(formValues.editable_fields),
        product_link: formValues.product_link
          ? [].concat(formValues.product_link)
          : [],
      };
      setPendingProductInfo(productInfo);
      setView("environment-details");
      return;
    }

    if (view === "environment-details") {
      const environmentInfo = {
        env_sample_supply: formValues.env_sample_supply,
        sample_type: formValues.sample_type,
        date: formValues.date,
        physical_category: formValues.physical_category,
        swab_number: formValues.swab_number,
        zone: formValues.zone,
        section: formValues.section,
        editable_fields: toEditableFieldObjects(formValues.editable_fields),
        environment_link: formValues.environment_link || [],
        environment_supplies_needed: formValues.environment_supplies_needed,
      };
      setPendingEnvironmentInfo(environmentInfo);
      // Add More Location is always 2nd+ — collect billing from the user
      setView("billing");
      return;
    }

    if (view === "billing") {
      const billingInfo = {
        contact_name: formValues.contact_name,
        position: formValues.position,
        email: formValues.email,
        phone_number: formValues.phone_number,
        same_as_address: formValues.same_as_address,
        billing: formValues.billing,
      };
      handleSaveLocation(
        pendingDetails || {},
        pendingProductInfo,
        pendingEnvironmentInfo,
        billingInfo,
      );
      return;
    }

    const firstIncomplete = locations.find((location) => !isLocationComplete(location));
    if (firstIncomplete) {
      setShowIncomplete(true);
      toast.error(`Please complete the details for "${firstIncomplete.name || "this location"}" before continuing.`);
      return;
    }

    if (hasDuplicateCompanyNames(locations)) {
      toast.error(DUPLICATE_COMPANY_NAME_MESSAGE);
      return;
    }

    const values = { locations };
    if (addCompany) {
      handleSubmit(FORM_NAME, values);
    } else {
      onNextButton(FORM_NAME, values);
    }
  };

  const handleBack = () => {
    if (view === "add") {
      handleCancelAdd();
      return;
    }
    if (view === "details") {
      if (isEditing) {
        handleCancelAdd();
        return;
      }
      if (skipManagementChoice) {
        handleCancelAdd();
        return;
      }
      setView("add");
      return;
    }
    if (view === "product-details") {
      setView("details");
      return;
    }
    if (view === "environment-details") {
      setView("product-details");
      return;
    }
    if (view === "billing") {
      setView("environment-details");
      return;
    }
    onBackButton(FORM_NAME, { locations });
  };

  const nextButtonText = (() => {
    if (view === "billing") return "Save";
    if (view === "product-details") return "Next 1/2";
    if (view === "environment-details") return "Next 2/2";
    if (view === "add" || view === "details" || !addCompany) return "Next";
    return "Create Company";
  })();

  const backButtonText = (() => {
    if (view === "billing") return "Cancel";
    if (view === "details" && isEditing) return "Cancel";
    if (isAddFlowView) return "Previous";
    return "Cancel";
  })();

  const sampleSupply = Form.useWatch(
    view === "environment-details" ? "env_sample_supply" : "sample_supply",
    form,
  );
  const nextDisabled = (view === "add" && !managementType)
    || ((view === "product-details" || view === "environment-details") && !sampleSupply);

  const renderBody = () => {
    if (view === "add") {
      return <AddLocationChoice selected={managementType} onSelect={setManagementType} />;
    }
    if (view === "details") {
      return (
        <AddLocationDetails
          companyInfo={companyInfo}
          managementType={managementType}
        />
      );
    }
    if (view === "product-details") {
      return <LocationSampleDetails type="product" />;
    }
    if (view === "environment-details") {
      return <LocationSampleDetails type="environment" />;
    }
    if (view === "billing") {
      return (
        <LocationBillingDetails
          emailValidator={emailValidator}
          phoneValidator={phoneValidator}
          addressData={pendingDetails?.address || {}}
          initialValues={billingInitialValues()}
        />
      );
    }
    return (
      <div>
        <div className="OnboardingFormSubTitle">
          <span />
          <p className="OnboardingFormSubTitleText">Your Locations</p>
        </div>

        <div className="LocationsForm__List">
          {locations.map((location, index) => (
            <LocationCard
              key={location.id}
              location={location}
              incomplete={showIncomplete && !isLocationComplete(location)}
              onClick={() => handleOpenEdit(location)}
              canDelete={canDeleteLocation(location, index)}
              onDelete={handleDeleteLocation}
            />
          ))}

          {canAddLocation && (
            <div className="LocationsForm__AddCard">
              <div className="LocationsForm__AddCardHeader">
                <BuildingIcon className="LocationsForm__BuildingIcon LocationsForm__BuildingIcon--muted" />
                <span>Add More Location</span>
              </div>
              <Button
                type="default"
                htmlType="button"
                className="LocationsForm__AddBtn"
                onClick={handleOpenAdd}
              >
                Add
              </Button>
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <MyForm
      {...layout} // eslint-disable-line react/jsx-props-no-spreading
      form={form}
      initialValues={{ locations }}
      requiredMark={false}
      name={FORM_NAME}
      onFinish={handleFinish}
      onFinishFailed={onFinishFailed}
      className="OnboardingForm LocationsForm"
    >
      {renderBody()}

      <Form.Item>
        <FormButtons
          handleBack={handleBack}
          loading={submitting}
          nextDisabled={nextDisabled}
          nextButtonText={nextButtonText}
          backButtonText={backButtonText}
        />
      </Form.Item>
    </MyForm>
  );
}
